import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { ProductGridSkeleton } from '../components/Skeleton';
import ErrorState from '../components/ErrorState';
import EmptyState from '../components/EmptyState';
import usePageMetadata from '../hooks/usePageMetadata';
import * as catalogApi from '../api/endpoints/catalog';
import { useCart } from '../context/CartContext';
import { containerWide, focusRing, pageHeading, pageSection, selectClass } from '../styles/ui';

const DEFAULT_SORT = 'newest';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'discount_desc', label: 'Discount' },
  { value: 'popularity', label: 'Popularity' },
  { value: 'customer_reviews', label: 'Top Rated' },
];

const CategoryDetailPage = () => {
  const { categoryKey } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const sort = searchParams.get('sort') || DEFAULT_SORT;
  const subCategory = searchParams.get('subCategory') || '';
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const [subCategories, setSubCategories] = useState([]);
  const { addItem, setQuantity, quantityFor } = useCart();
  const requestIdRef = useRef(0);

  usePageMetadata({
    title: `${categoryKey} | Fresh & Homely`,
    description: 'Browse products at Fresh & Homely.',
    canonicalPath: `/categories/${categoryKey}`,
  });

  // Sub-category chips are entirely data-driven — most admin categories have
  // none (Seafood is the current exception, with Fish/Prawns/Crustaceans), so
  // the row only renders when the backend actually reports options for this
  // category rather than a hardcoded list.
  useEffect(() => {
    let active = true;
    catalogApi
      .listCategories()
      .then((response) => {
        if (!active) return;
        const match = (response.data || []).find((c) => c.key === categoryKey);
        setSubCategories(match?.subCategories || []);
      })
      .catch(() => {
        if (active) setSubCategories([]);
      });
    return () => {
      active = false;
    };
  }, [categoryKey]);

  const load = () => {
    const requestId = ++requestIdRef.current;
    setState({ status: 'loading', data: null, error: null });
    catalogApi
      .listProducts({ category: categoryKey, subCategory: subCategory || undefined, sort, limit: 40 })
      .then((response) => {
        if (requestId !== requestIdRef.current) return; // superseded by a newer filter/sort change
        setState({ status: 'success', data: response.data, error: null });
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        setState({ status: 'error', data: null, error: err.message });
      });
  };

  useEffect(load, [categoryKey, subCategory, sort]);

  const handleSubCategorySelect = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key) next.set('subCategory', key);
    else next.delete('subCategory');
    setSearchParams(next);
  };

  // Merge into the existing params rather than replacing them — passing a bare
  // object to setSearchParams dropped every other key, so changing the sort
  // silently cleared the selected sub-category.
  const handleSortChange = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== DEFAULT_SORT) next.set('sort', value);
    else next.delete('sort');
    setSearchParams(next);
  };

  return (
    <>
      <Navbar />
      <section className={pageSection}>
        <div className={containerWide}>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
            <h1 className={`${pageHeading} capitalize`}>
              {categoryKey.replace(/-/g, ' ')}
            </h1>
            <select
              value={sort}
              onChange={(e) => handleSortChange(e.target.value)}
              aria-label="Sort products"
              className={selectClass}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  Sort: {option.label}
                </option>
              ))}
            </select>
          </div>

          {subCategories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-10">
              <button
                type="button"
                onClick={() => handleSubCategorySelect('')}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${focusRing} ${
                  !subCategory
                    ? 'bg-gradient-primary text-white border-transparent shadow-soft'
                    : 'bg-white text-emerald-800 border-emerald-200 hover:bg-emerald-50'
                }`}
              >
                All
              </button>
              {subCategories.map((sub) => (
                <button
                  key={sub.key}
                  type="button"
                  onClick={() => handleSubCategorySelect(sub.key)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${focusRing} ${
                    subCategory === sub.key
                      ? 'bg-gradient-primary text-white border-transparent shadow-soft'
                      : 'bg-white text-emerald-800 border-emerald-200 hover:bg-emerald-50'
                  }`}
                >
                  {sub.label}
                </button>
              ))}
            </div>
          )}

          {state.status === 'loading' && <ProductGridSkeleton count={8} />}
          {state.status === 'error' && <ErrorState message={state.error} onRetry={load} />}
          {state.status === 'success' && state.data.length === 0 && (
            <EmptyState title="No products yet" description="Check back soon — this category is being stocked." />
          )}
          {state.status === 'success' && state.data.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {state.data.map((product) => (
                <ProductCard
                  key={product._id}
                  product={product}
                  quantity={quantityFor(product._id)}
                  onAddToCart={addItem}
                  onSetQuantity={setQuantity}
                />
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
};

export default CategoryDetailPage;
